import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, Check, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useStableAuth } from "@/hooks/useStableAuth";
import { useProductUnits } from "@/hooks/useProductUnits";
import { resolveMeasurementUnitId } from "@/lib/unitResolve";
import { QuickProductUnitDialog } from "./QuickProductUnitDialog";

// I.151 1b3-b: unidade do item da nota = base do produto ou alternativa cadastrada. A nota vai
// ao banco como veio (qCom/vUnCom/uCom) + a unidade escolhida; o banco converte e memoriza o
// vínculo (supplier_link_learn). A tela só mostra o fator — a conta aparece apenas na
// confirmação "É a base mesmo" (decisão do Diretor, 10/10), que fica memorizada por
// fornecedor + produto (supplier_product_links.base_confirmed_at).
// Item sem unidade definida (productUnitId === undefined) BLOQUEIA o avanço do assistente.

/** Vínculo do fornecedor para o código do item (o que interessa à unidade). */
export interface ImportLinkUnitInfo {
  internal_product_id: string | null;
  product_unit_id: string | null;
  supplier_unit: string | null;
  conversion_factor: number | null;
  conversion_operation: string | null;
  base_confirmed_at: string | null;
}

export interface ImportItemUnitChoice {
  /** null = unidade base; string = alternativa; undefined = não definida (bloqueia) */
  productUnitId: string | null | undefined;
  /** sigla exibida no resumo (base ou alternativa) */
  unitLabel?: string;
}

interface ImportItemUnitPickerProps {
  productId: string;
  uCom: string;
  quantity: number;
  productUnitId: string | null | undefined;
  link?: ImportLinkUnitInfo | null;
  onChange: (choice: ImportItemUnitChoice) => void;
}

const BASE = "__base__";
const fmt = (n: number) => n.toLocaleString("pt-BR", { maximumFractionDigits: 6 });
const norm = (s: string | null | undefined) => (s ?? "").trim().toUpperCase();

export function ImportItemUnitPicker({ productId, uCom, quantity, productUnitId, link, onChange }: ImportItemUnitPickerProps) {
  const { companyId } = useStableAuth();
  const { base, units, isLoading } = useProductUnits(productId);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogDefaults, setDialogDefaults] = useState<{ unitId: string | null; factor: number | null }>({
    unitId: null,
    factor: null,
  });

  const { data: uComUnitId, isFetched: uComResolved } = useQuery({
    queryKey: ["unit-resolve", companyId, norm(uCom)],
    queryFn: () => resolveMeasurementUnitId(companyId, uCom),
    enabled: !!companyId && !!norm(uCom),
    staleTime: 5 * 60 * 1000,
  });
  const uComReady = !norm(uCom) || uComResolved;

  const eligible = units.filter((u) => u.active && u.allow_purchase && !u.is_approximate && u.factor_to_base);
  const sameProductLink = link && link.internal_product_id === productId ? link : null;
  const legacyFactor =
    sameProductLink && !sameProductLink.product_unit_id && Number(sameProductLink.conversion_factor ?? 1) !== 1
      ? Number(sameProductLink.conversion_factor)
      : null;
  const memorizedBase =
    !!sameProductLink &&
    !sameProductLink.product_unit_id &&
    !!sameProductLink.base_confirmed_at &&
    norm(sameProductLink.supplier_unit) === norm(uCom);

  // Resolução automática — só enquanto o item não tem unidade definida
  useEffect(() => {
    if (productUnitId !== undefined || isLoading || !base || !uComReady) return;
    const linkUnit = sameProductLink?.product_unit_id
      ? eligible.find((u) => u.id === sameProductLink.product_unit_id)
      : undefined;
    if (linkUnit) return onChange({ productUnitId: linkUnit.id, unitLabel: linkUnit.sigla });
    if (legacyFactor) return; // fator antigo sem unidade: decisão do operador
    if (uComUnitId && uComUnitId === base.id) return onChange({ productUnitId: null, unitLabel: base.sigla });
    const alt = uComUnitId ? eligible.find((u) => u.measurement_unit_id === uComUnitId) : undefined;
    if (alt) return onChange({ productUnitId: alt.id, unitLabel: alt.sigla });
    if (memorizedBase) return onChange({ productUnitId: null, unitLabel: base.sigla });
    // senão: fica indefinido e bloqueia
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productUnitId, isLoading, base?.id, uComReady, uComUnitId, units.length, sameProductLink?.product_unit_id, legacyFactor, memorizedBase]);

  if (!productId) return null;

  const openRegister = (factor: number | null) => {
    setDialogDefaults({ unitId: uComUnitId ?? null, factor });
    setDialogOpen(true);
  };

  const value = productUnitId === undefined ? "" : productUnitId === null ? BASE : productUnitId;
  const chosen = productUnitId ? eligible.find((u) => u.id === productUnitId) : undefined;
  const uComIsBase = !!base && !!uComUnitId && uComUnitId === base.id;
  const baseNeedsConta = productUnitId === null && !uComIsBase && norm(uCom) !== norm(base?.sigla);

  return (
    <div className="space-y-2">
      <Label className="text-xs">Unidade</Label>
      <Select
        value={value}
        onValueChange={(v) => {
          if (v === BASE) onChange({ productUnitId: null, unitLabel: base?.sigla });
          else {
            const u = eligible.find((x) => x.id === v);
            if (u) onChange({ productUnitId: u.id, unitLabel: u.sigla });
          }
        }}
      >
        <SelectTrigger className="h-8 w-56">
          <SelectValue placeholder="Definir unidade" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={BASE}>{base?.sigla || "base"} (base)</SelectItem>
          {eligible.map((u) => (
            <SelectItem key={u.id} value={u.id}>
              {u.sigla} · 1 = {fmt(Number(u.factor_to_base))} {base?.sigla}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {chosen?.factor_to_base ? (
        <p className="text-xs text-muted-foreground">
          1 {chosen.sigla} = {fmt(Number(chosen.factor_to_base))} {base?.sigla} · quantidade e preço da nota em {chosen.sigla}
        </p>
      ) : null}

      {baseNeedsConta && (
        <p className="text-xs text-amber-700">
          {fmt(quantity)} {uCom} vão entrar como {fmt(quantity)} {base?.sigla}
          {memorizedBase && sameProductLink?.base_confirmed_at
            ? ` · confirmado em ${new Date(sameProductLink.base_confirmed_at).toLocaleDateString("pt-BR")}`
            : ""}
        </p>
      )}

      {legacyFactor && productUnitId === undefined && (
        <div className="rounded border border-blue-200 bg-blue-50 p-2 text-xs text-blue-800 space-y-2">
          <p>
            Este fornecedor usava fator {fmt(legacyFactor)} para este item (1 {uCom || "unidade da nota"} ={" "}
            {fmt(legacyFactor)} {base?.sigla}). Cadastre a unidade para o sistema converter.
          </p>
          <Button type="button" size="sm" variant="outline" className="h-7" onClick={() => openRegister(legacyFactor)}>
            <Plus className="h-3 w-3 mr-1" />
            Cadastrar {uCom || "unidade"} = {fmt(legacyFactor)} {base?.sigla}
          </Button>
        </div>
      )}

      {productUnitId === undefined && !isLoading && base && uComReady && (
        <div className="rounded border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800 space-y-2">
          <p className="flex items-center gap-1">
            <AlertTriangle className="h-3 w-3" />
            Unidade da nota {uCom || "(vazia)"} não é a base ({base.sigla}) nem uma unidade cadastrada do produto.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-7"
              onClick={() => onChange({ productUnitId: null, unitLabel: base.sigla })}
            >
              <Check className="h-3 w-3 mr-1" />
              É a base mesmo: {fmt(quantity)} {uCom} = {fmt(quantity)} {base.sigla}
            </Button>
            <Button type="button" size="sm" variant="outline" className="h-7" onClick={() => openRegister(null)}>
              <Plus className="h-3 w-3 mr-1" />
              Cadastrar {uCom || "unidade"}
            </Button>
          </div>
        </div>
      )}

      <QuickProductUnitDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        productId={productId}
        defaultMeasurementUnitId={dialogDefaults.unitId}
        defaultFactor={dialogDefaults.factor}
        onCreated={(id) => {
          const u = units.find((x) => x.id === id);
          onChange({ productUnitId: id, unitLabel: u?.sigla ?? norm(uCom) });
        }}
      />
    </div>
  );
}
